import type { SearchItineraryStore } from '$houdini';
import type { ItineraryResultsStore } from '$lib/components/itinerary/types';
import type { Route, TrafficMentionWithGeometry } from '$lib/components/map/types';
import type { SearchParams } from '$lib/services/urls/searchParams';
import type { AugmentedHoudiniStore } from '$lib/utils/augmentedHoudiniStore';
import type { LayoutLoadEvent as CardsLayoutLoadEvent } from './$types';
import type { LayoutLoadEvent } from './(details)/$types';
import type { PageLoadEvent } from './results/[...addresses]/$types';
import type { WaypointsInfo } from './types';
import { browser } from '$app/environment';
import { getWaypointCoordinates } from '$lib/components/search/itinerary/ItineraryStore';
import { resetAdServedIds } from '$lib/services/advertising/orange/adserving';
import { getPartnersDeviceType } from '$lib/utils/getPartnersDeviceType';
import { filterUndefined } from '$lib/utils/predicates';
import mapboxPolyline from '@mapbox/polyline';
import { get } from 'svelte/store';

interface FetchSearchItineraryStoreArgs {
	waypointsInfo: WaypointsInfo;
	searchItineraryStore: AugmentedHoudiniStore<SearchItineraryStore>;
	searchParams: SearchParams;
	onFetchComplete?: () => void;
}

type LoadEvent = PageLoadEvent | LayoutLoadEvent | CardsLayoutLoadEvent;

export async function fetchSearchItineraryStore(
	event: LoadEvent,
	{ waypointsInfo, searchItineraryStore, searchParams, onFetchComplete }: FetchSearchItineraryStoreArgs,
): Promise<void> {
	if (browser) {
		const { waypoints: urlWaypoints, departure, arrival } = waypointsInfo;
		const coordinates = await Promise.all(urlWaypoints.map(getWaypointCoordinates));

		resetAdServedIds();

		await searchItineraryStore.fetch({
			event,
			variables: {
				input: {
					coordinates: coordinates.filter(filterUndefined),
					departureName: departure.name,
					arrivalName: arrival.name,
					carId: searchParams.car?.id,
					constraint: searchParams.tripConstraint,
					distanceSystem: searchParams.distanceSystem,
					mode: searchParams.travelMode,
					device: getPartnersDeviceType(),
					// TODO: replace with values from user input in trip options
					energyCost: searchParams.energyPrice,
					currency: searchParams.currency,
					rechargeTreshold: searchParams.rechargeTreshold,
					withCaravan: searchParams.withCaravan,
					traffic: searchParams.traffic,
				},
			},
		});

		onFetchComplete?.();
	}
}

interface GetItineraryResultsStoreArgs {
	searchItineraryStore: AugmentedHoudiniStore<SearchItineraryStore>;
}

export function getItineraryResultsStore({ searchItineraryStore }: GetItineraryResultsStoreArgs) {
	return searchItineraryStore.derive(($store) => {
		if (
			!$store.data
			|| $store.data.searchItinerary.__typename === 'SearchItineraryNotFoundResult'
		) {
			return {
				fetching: $store.fetching,
				routes: [],
				hasError: !!$store.errors,
				partners: {
					cards: [],
				},
				adsTextLines: [],
				closingsMessage: { text: '' },
				waypoints: [],
				isDefaultValue: true,
			};
		}

		const routes = $store.data.searchItinerary.routes.map((route: Route, index: number) => ({
			...route,
			geometry: mapboxPolyline.toGeoJSON(route.geometry),
			encodedGeometry: route.geometry,
			trafficMentions: (route?.trafficMentions?.filter(Boolean).map((trafficMention: NonNullable<Route['trafficMentions']>[number]) => trafficMention && ({
				...trafficMention,
				geometry: { line: mapboxPolyline.toGeoJSON(trafficMention.geometry), index },
				encodedGeometry: trafficMention.geometry,
				color: trafficMention.color,
			})) ?? []) as TrafficMentionWithGeometry[],
			index,
		}));
		const waypoints = $store.data.searchItinerary.waypoints;

		return {
			fetching: $store.fetching,
			hasError: !!$store.errors,
			waypoints,
			routes,
			arrivalZoi: $store.data.searchItinerary.arrivalZoi ?? undefined,
			departureAddress: $store.data.searchItinerary.departureAddress,
			arrivalAddress: $store.data.searchItinerary.arrivalAddress,
			adsTextLines: $store.data.searchItinerary.adsTextLines,
			closingsMessage: $store.data.searchItinerary.closingsMessage,
			departureCityPath: $store.data.searchItinerary.departureCityPath,
			arrivalCityPath: $store.data.searchItinerary.arrivalCityPath,
			isDefaultValue: false,
		};
	});
}

export function itineraryResultsStoreUtils(itineraryResultsStore: ItineraryResultsStore) {
	const itineraryResultsData = get(itineraryResultsStore);

	return {
		getDepartureWaypoint() {
			return itineraryResultsData.waypoints[0];
		},
		getArrivalWaypoint() {
			return itineraryResultsData.waypoints.at(-1);
		},
	};
}
